import {
    Ban,
    BriefcaseBusiness,
    EyeOff,
    HeartHandshake,
    Landmark,
    LifeBuoy,
    Mountain,
    Shield,
    Sparkles,
    Sun,
    TrendingUp,
    WandSparkles,
} from "lucide-react";

export type HealingService = {
    slug: string;
    title: string;
    shortDescription: string;
    description: string;
    icon: typeof Sparkles;
    accent: string;
    focus: string[];
};

export const additionalHealingServices: HealingService[] = [
    {
        slug: "relationship-bonding",
        title: "Relationship Bonding",
        shortDescription: "Strengthen emotional connection, trust, and mutual understanding.",
        description:
            "A focused healing for couples or relationship dynamics where emotional distance, repeated misunderstandings, or weak bonding need gentle energetic support.",
        icon: HeartHandshake,
        accent: "from-cyan-300 to-indigo-500",
        focus: ["Emotional closeness", "Trust rebuilding", "Calmer communication"],
    },
    {
        slug: "delay-in-marriage",
        title: "Delay in Marriage",
        shortDescription: "Energetic support for marriage delays, blocks, and repeated obstacles.",
        description:
            "A healing for people experiencing repeated delays, uncertainty, or energetic resistance around marriage alignment and suitable proposals.",
        icon: Landmark,
        accent: "from-rose-400 to-amber-300",
        focus: ["Marriage block clearing", "Proposal alignment", "Family harmony"],
    },
    {
        slug: "remove-evil-eye",
        title: "Remove Evil Eye",
        shortDescription: "Cleanse nazar, jealousy, and draining external attention.",
        description:
            "A protective cleansing session intended to release evil eye effects, jealousy, and heavy attention that may be affecting energy, mood, work, or peace.",
        icon: EyeOff,
        accent: "from-sky-300 to-violet-600",
        focus: ["Nazar removal", "Aura cleansing", "Energy refresh"],
    },
    {
        slug: "clear-black-magic",
        title: "Clear Any Black Magic",
        shortDescription: "Deep spiritual cleansing for suspected black magic influence.",
        description:
            "A stronger clearing-focused healing for dense spiritual interference, recurring heaviness, fear, stagnation, and unexplained energetic disturbance.",
        icon: WandSparkles,
        accent: "from-fuchsia-500 to-red-700",
        focus: ["Spiritual cleansing", "Heavy energy release", "Protective grounding"],
    },
    {
        slug: "protection-from-evil-forces",
        title: "Protection from Evil Forces",
        shortDescription: "Create a stronger energetic shield and spiritual protection field.",
        description:
            "A protection-oriented healing for people who feel vulnerable to negative energy, spiritual disturbance, or repeated harmful influences.",
        icon: Shield,
        accent: "from-orange-300 to-red-600",
        focus: ["Protection shield", "Grounding", "Energetic boundaries"],
    },
    {
        slug: "clear-obstacles",
        title: "Clear Obstacles",
        shortDescription: "Release resistance and repeated blocks across important life areas.",
        description:
            "A clearing session for people facing repeated delays, blocked opportunities, stuck decisions, or resistance that keeps progress from stabilizing.",
        icon: Mountain,
        accent: "from-teal-300 to-emerald-600",
        focus: ["Path clearing", "Decision clarity", "Momentum restoration"],
    },
    {
        slug: "business-success",
        title: "Business Success",
        shortDescription: "Energetic alignment for growth, clients, cash flow, and stability.",
        description:
            "A business-focused healing for founders, shop owners, freelancers, and professionals seeking cleaner momentum, client attraction, and stronger stability.",
        icon: BriefcaseBusiness,
        accent: "from-amber-300 to-orange-600",
        focus: ["Client attraction", "Cash flow energy", "Business stability"],
    },
    {
        slug: "career-growth",
        title: "Career Growth",
        shortDescription: "Support recognition, promotion, confidence, and professional progress.",
        description:
            "A career-focused healing for people seeking better opportunities, visibility, confidence, and steady professional movement.",
        icon: TrendingUp,
        accent: "from-blue-300 to-indigo-600",
        focus: ["Career momentum", "Recognition", "Confidence"],
    },
    {
        slug: "recover-losses",
        title: "Recover Losses",
        shortDescription: "Spiritual support after financial, business, or emotional setbacks.",
        description:
            "A recovery-focused healing for people trying to regain stability after losses, failed opportunities, setbacks, or heavy emotional impact.",
        icon: LifeBuoy,
        accent: "from-lime-300 to-emerald-600",
        focus: ["Recovery energy", "Stability", "New opportunity alignment"],
    },
    {
        slug: "resolve-severe-life-issues",
        title: "Resolve Severe Life Issues",
        shortDescription: "Focused healing support for complex, heavy, repeated life problems.",
        description:
            "A deeper support container for intense or recurring life issues where multiple areas feel blocked and emotional strength is being tested.",
        icon: Ban,
        accent: "from-red-500 to-purple-700",
        focus: ["Complex issue support", "Emotional strength", "Block clearing"],
    },
    {
        slug: "blissful-life",
        title: "Blissful Life",
        shortDescription: "Invite peace, harmony, gratitude, and smoother daily energy.",
        description:
            "A gentle healing for people seeking more peace, emotional balance, inner softness, and harmonious daily living.",
        icon: Sun,
        accent: "from-yellow-300 to-rose-400",
        focus: ["Peaceful energy", "Harmony", "Emotional balance"],
    },
];
